import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { turkishBadWordsFilter } from '../../utils/turkishBadWordsFilter';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../Firebase';
import styles from './HomeApplicationForm.module.css';
import applicationImage from '../../assets/images/home.png';

const HomeApplicationForm = () => {
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        address: ''
    });
    const [inputErrors, setInputErrors] = useState({
        name: '',
        phone: '',
        address: ''
    });
    const [agreement, setAgreement] = useState(false);

    const validateField = (name, value) => {
        if (name === 'phone') {
            const phoneRegex = /^5[0-9]{2}\s?[0-9]{3}\s?[0-9]{2}\s?[0-9]{2}$/;
            if (!phoneRegex.test(value)) {
                return 'Lütfen geçerli bir telefon numarası giriniz (5XX XXX XX XX)';
            }
        } else if (name === 'name' || name === 'address') {
            if (turkishBadWordsFilter.isProfane(value)) {
                return 'Lütfen uygun bir dil kullanınız';
            }
        }

        if (name === 'name' && value.trim().length < 2) {
            return 'Lütfen geçerli bir isim giriniz';
        }

        if (name === 'address' && value.trim().length < 10) {
            return 'Lütfen daha detaylı bir adres giriniz';
        }

        return '';
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setInputErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = { ...inputErrors };

        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            newErrors[key] = error;
            if (error) isValid = false;
        });

        if (!agreement) {
            toast.error('Lütfen gizlilik politikasını kabul edin');
            isValid = false;
        }

        setInputErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Lütfen formdaki hataları düzeltin');
            return;
        }

        setSubmitting(true);

        try {
            // Firebase'e başvuruyu kaydet
            await addDoc(collection(db, 'applications'), {
                address: formData.address,
                applicationDate: serverTimestamp(),
                name: formData.name,
                phone: formData.phone.replace(/\s/g, ''), // Boşlukları kaldır
                processed: false, // Başlangıçta false olacak
                status: 'pending', // Başlangıçta pending olacak
                isClean: !turkishBadWordsFilter.isProfane(JSON.stringify(formData)),
                source: 'homepage_form', // Başvurunun kaynağını belirtmek için
                formType: 'home_application'
            });

            // Formu temizle
            setFormData({
                name: '',
                phone: '',
                address: ''
            });
            setAgreement(false);

            toast.success('Başvurunuz başarıyla alındı! En kısa sürede sizinle iletişime geçilecektir.');
        } catch (error) {
            console.error('Başvuru kaydedilirken hata:', error);
            toast.error('Başvuru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
        } finally {
            setSubmitting(false);
        }
    };

    const formatPhoneNumber = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `${numbers.slice(0, 3)} ${numbers.slice(3)}`;
        if (numbers.length <= 8) return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
        return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6, 8)} ${numbers.slice(8, 10)}`;
    };

    const handlePhoneChange = (e) => {
        const formattedValue = formatPhoneNumber(e.target.value);
        setFormData(prev => ({ ...prev, phone: formattedValue }));
        setInputErrors(prev => ({ ...prev, phone: validateField('phone', formattedValue) }));
    };

    return (
        <section
            className={styles.applicationSection}
            aria-labelledby="home-application-title"
        >
            <ToastContainer position="top-right" autoClose={5000} />

            <div className={styles.container}>
                <div className={styles.contentGrid}>

                    {/* FORM */}
                    <div className={styles.formColumn}>
                        <header className={styles.formHeader}>
                            <h1 id="home-application-title" className={styles.mainTitle}>
                                Evde Fiber İnternet Başvurusu
                            </h1>
                            <p className={styles.formSubtitle}>
                                Fiber internet altyapınızı sorgulayın, size en uygun kampanya için hemen başvurun.
                            </p>
                        </header>

                        <form
                            onSubmit={handleSubmit}
                            className={styles.applicationForm}
                            aria-label="Fiber internet başvuru formu"
                        >
                                <div className={styles.formRow}>
                                    <div className={styles.inputWrapper}>
                                        <label htmlFor="name">Ad Soyad</label>
                                        <input
                                            id="name"
                                            name="name"
                                            type="text"
                                            autoComplete="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            inputMode="text"
                                            className={`${styles.input} ${inputErrors.name ? styles.error : ''}`}
                                        />
                                        {inputErrors.name && (
                                            <span className={styles.errorMessage}>{inputErrors.name}</span>
                                        )}
                                    </div>

                                    <div className={styles.inputWrapper}>
                                        <label htmlFor="phone">Telefon Numarası</label>
                                        <input
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            autoComplete="tel"
                                            inputMode="tel"
                                            value={formData.phone}
                                            onChange={handlePhoneChange}
                                            required
                                            className={`${styles.input} ${inputErrors.phone ? styles.error : ''}`}
                                        />
                                        {inputErrors.phone && (
                                            <span className={styles.errorMessage}>{inputErrors.phone}</span>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.inputWrapper}>
                                    <label htmlFor="address">Adres Bilgisi</label>
                                    <textarea
                                        id="address"
                                        name="address"
                                        autoComplete="street-address"
                                        rows="2"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        required
                                        className={`${styles.textarea} ${inputErrors.address ? styles.error : ''}`}
                                    />
                                    {inputErrors.address && (
                                        <span className={styles.errorMessage}>{inputErrors.address}</span>
                                    )}
                                </div>

                                <div className={styles.footerRow}>
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={agreement}
                                            onChange={(e) => setAgreement(e.target.checked)}
                                                className={styles.checkbox}
                                                required
                                        />
                                        <span className={styles.checkmark}></span>
                                        <a href="/kvkk" target="_blank" rel="noopener noreferrer">
                                            Gizlilik Politikası
                                        </a>
                                            'nı kabul ediyorum.
                                    </label>

                                    <button
                                        type="submit"
                                        className={styles.submitButton}
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <span className={styles.loadingSpinner} />
                                        ) : (
                                            <>
                                                Fiber İnternete Hemen Başvur
                                                <svg
                                                    width="16"
                                                    height="14"
                                                    viewBox="0 0 16 14"
                                                    className={styles.arrowIcon}
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        d="M1 7H15M15 7L9 1M15 7L9 13"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    />
                                                </svg>
                                            </>
                                        )}
                                    </button>
                                </div>
                        </form>
                    </div>

                    {/* GÖRSEL */}
                    <figure className={styles.imageColumn}>
                        <img
                            src={applicationImage}
                            alt="Evde hızlı ve uygun fiyatlı fiber internet başvurusu"
                            loading="lazy"
                            width="420"
                            height="420"
                        />
                        <figcaption className={styles.imageBadge}>
                            ⚡ Hızlı Kurulum • 💰 Uygun Fiyat
                        </figcaption>
                    </figure>

                </div>
            </div>
        </section>

    );
};

export default HomeApplicationForm;